import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw, Send, ShieldAlert } from 'lucide-react';

import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';

/**
 * Composer mínimo de saída — validação da Fase 5.
 *
 * NÃO é a caixa de entrada. A tela de verdade é a Fase 3 (três colunas, lista
 * virtualizada, realtime), e esta aqui é deliberadamente pobre para não virar
 * meia tela que depois atrapalhe aquela: escolher conversa, digitar, enviar,
 * ver o status. Nada mais.
 *
 * O que ela existe para provar, e que só se prova com provider real:
 *  - a mensagem enviada daqui chega no celular;
 *  - a política do canal recusa ANTES de gravar, com mensagem legível;
 *  - o erro do `functions.invoke` não vira "non-2xx status code" na cara do
 *    usuário.
 *
 * REGRA DO MÓDULO, respeitada aqui: o composer é dirigido por CAPABILITIES,
 * nunca por `channel_type`. Não há um único `if (canal === 'whatsapp_...')`
 * neste arquivo — assunto, cópia e corpo HTML aparecem porque a conexão diz
 * que sabe fazer, não porque alguém decorou qual canal tem o quê. Quando o
 * e-mail entrar na Fase 7, esta tela ganha os campos sem uma linha de código.
 */

interface ConexaoPublica {
  id: string;
  display_name: string;
  channel_type: string;
  status: string;
  capabilities: Record<string, boolean | string> | null;
}

interface ConversaLinha {
  id: string;
  connection_id: string;
  kind: string;
  subject: string | null;
  window_expires_at: string | null;
  last_message_at: string | null;
}

interface MensagemLinha {
  id: string;
  direction: string;
  type: string;
  body: string | null;
  status: string | null;
  failure_reason: string | null;
  provider_timestamp: string;
}

const CORES_STATUS: Record<string, string> = {
  queued: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  sent: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  delivered: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  read: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};

/**
 * Desembrulha o motivo real de um erro do `functions.invoke`.
 *
 * Em resposta não-2xx o SDK devolve só "Edge Function returned a non-2xx
 * status code" e joga o corpo em `error.context`. Aqui isso importa mais que
 * no resto do sistema: o 422 do `assertCanSend` traz exatamente a frase que o
 * atendente precisa ler ("janela de 24h expirada, use um template"). Sem este
 * desembrulho, a política do canal vira erro genérico.
 *
 * Mesmo padrão de src/lib/gatewayResolver.ts.
 */
async function motivoDoErro(error: unknown): Promise<string> {
  const detalhe = await (error as any)?.context?.json?.().catch(() => null);
  if (detalhe?.error) return String(detalhe.error);
  return (error as any)?.message || 'Falha ao enviar.';
}

const ComposerTesteView: React.FC = () => {
  const { profile } = useAuth();

  // O RoleGuard da rota NÃO basta: ele só bloqueia quando `profile.role`
  // existe (`if (profile?.role && blocked.includes(...))`), e o AuthContext
  // destrava a UI por timeout de 8s mesmo quando o fetch do profile falha.
  // Com `profile` null o guard deixa passar.
  //
  // No /omnichannel isso exporia uma tela de leitura. Aqui exporia o ENVIO:
  // um vendedor com a rede oscilando no login chegaria a esta rota e mandaria
  // mensagem pelo WhatsApp da Vigor. O `inbox-send` ainda recusaria pela RLS,
  // mas só enquanto ele não for membro do Inbox por outro motivo — e uma tela
  // de envio não deve ficar alcançável por quem não deveria vê-la.
  const ehAdmin = profile?.role === 'adm';

  const [conexoes, setConexoes] = useState<ConexaoPublica[]>([]);
  const [conversas, setConversas] = useState<ConversaLinha[]>([]);
  const [conversaId, setConversaId] = useState<string>('');
  const [mensagens, setMensagens] = useState<MensagemLinha[]>([]);

  const [texto, setTexto] = useState('');
  const [assunto, setAssunto] = useState('');
  const [copia, setCopia] = useState('');

  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);

    // `inbox_connections_public`, nunca `inbox_channel_connections`: a tabela
    // tem `credentials_ref` e o grant é por coluna. Query direta ao segredo
    // devolve permission denied — e credencial não passa pelo browser.
    const [{ data: conns, error: erroConns }, { data: convs, error: erroConvs }] =
      await Promise.all([
        supabase
          .from('inbox_connections_public')
          .select('id, display_name, channel_type, status, capabilities'),
        supabase
          .from('inbox_conversations')
          .select('id, connection_id, kind, subject, window_expires_at, last_message_at')
          .order('last_message_at', { ascending: false, nullsFirst: false })
          .limit(50),
      ]);

    if (erroConns || erroConvs) {
      setErro((erroConns || erroConvs)?.message ?? 'Falha ao carregar.');
    }

    setConexoes(conns || []);
    setConversas(convs || []);
    setCarregando(false);
  }, []);

  useEffect(() => {
    // Não consulta nada sem ser admin: sem isto a tela de acesso restrito
    // apareceria, mas as queries já teriam saído.
    if (ehAdmin) carregar();
    else setCarregando(false);
  }, [ehAdmin, carregar]);

  const carregarMensagens = useCallback(async (id: string) => {
    if (!id) {
      setMensagens([]);
      return;
    }
    // Ordenado por provider_timestamp, nunca por created_at: o horário que
    // importa é o do provider, e os dois divergem no eco do canal não-oficial.
    const { data } = await supabase
      .from('inbox_messages')
      .select('id, direction, type, body, status, failure_reason, provider_timestamp')
      .eq('conversation_id', id)
      .order('provider_timestamp', { ascending: true })
      .limit(100);
    setMensagens(data || []);
  }, []);

  useEffect(() => {
    carregarMensagens(conversaId);
  }, [conversaId, carregarMensagens]);

  const conversa = useMemo(
    () => conversas.find((c) => c.id === conversaId) ?? null,
    [conversas, conversaId],
  );

  const conexao = useMemo(
    () => (conversa ? conexoes.find((c) => c.id === conversa.connection_id) ?? null : null),
    [conexoes, conversa],
  );

  // A matriz do canal, lida do banco. O front NÃO decide o que um canal sabe
  // fazer — ele lê o snapshot que a conexão carrega.
  const caps = (conexao?.capabilities ?? {}) as Record<string, boolean | string>;

  const janelaExpirada = useMemo(() => {
    if (!caps.hasResponseWindow) return false;
    if (!conversa?.window_expires_at) return true;
    return new Date(conversa.window_expires_at).getTime() < Date.now();
  }, [caps.hasResponseWindow, conversa?.window_expires_at]);

  const enviar = async () => {
    if (!conversaId || !texto.trim() || enviando) return;
    setEnviando(true);
    setErro(null);
    setAviso(null);

    const payload: Record<string, unknown> = { type: 'text', body: texto.trim() };
    if (caps.supportsSubject && assunto.trim()) payload.subject = assunto.trim();
    if (caps.supportsCc && copia.trim()) {
      payload.recipients = { to: [], cc: copia.split(',').map((e) => e.trim()).filter(Boolean) };
    }

    try {
      const { data, error } = await supabase.functions.invoke('inbox-send', {
        body: { conversation_id: conversaId, payload },
      });
      if (error) throw new Error(await motivoDoErro(error));
      if ((data as any)?.error) throw new Error((data as any).error);

      setTexto('');
      setAviso('Enfileirada. O status vira "sent" quando o worker entregar.');
      await carregarMensagens(conversaId);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao enviar.');
    } finally {
      setEnviando(false);
    }
  };

  if (!ehAdmin) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center px-4">
        <ShieldAlert size={40} className="mx-auto text-gray-400 mb-4" />
        <h1 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Acesso restrito</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          O composer de teste envia mensagem para cliente real e é visível apenas
          para administradores.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 font-display">
            Composer de teste — Fase 5
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Valida o envio ponta a ponta. A caixa de entrada de verdade é a Fase 3.
          </p>
        </div>
        <button
          onClick={carregar}
          disabled={carregando}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors"
        >
          <RefreshCw size={13} className={carregando ? 'animate-spin' : ''} /> Recarregar
        </button>
      </header>

      {carregando ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-10 justify-center">
          <Loader2 size={16} className="animate-spin" /> Carregando conversas...
        </div>
      ) : conversas.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 p-8 text-center">
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Nenhuma conversa ainda.
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Só aparece conversa depois que a ingestão da Fase 2 estiver ligada e
            alguém mandar mensagem. Enquanto a caixa estiver zerada, não há o que
            responder.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
              Conversa
            </label>
            <select
              value={conversaId}
              onChange={(e) => setConversaId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-100"
            >
              <option value="">Selecione...</option>
              {conversas.map((c) => {
                const con = conexoes.find((x) => x.id === c.connection_id);
                return (
                  <option key={c.id} value={c.id}>
                    {con?.display_name ?? 'conexão desconhecida'} · {c.kind}
                    {c.subject ? ` · ${c.subject}` : ''}
                  </option>
                );
              })}
            </select>
          </div>

          {conversa && (
            <>
              {conexao && conexao.status !== 'active' && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
                  <AlertTriangle size={15} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    O canal está <strong>{conexao.status}</strong>. O envio será
                    recusado até reconectar.
                  </p>
                </div>
              )}

              {/* Banner de risco permanente do canal não-oficial. Vem de
                  capabilities.isUnofficial, não de uma lista de canais. */}
              {caps.isUnofficial && (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3">
                  <AlertTriangle size={15} className="text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-red-800 dark:text-red-200">
                    Canal <strong>não oficial</strong>: opera fora dos termos da
                    Meta e o número pode ser banido sem aviso.
                  </p>
                </div>
              )}

              {janelaExpirada && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
                  <AlertTriangle size={15} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    Janela de resposta expirada.{' '}
                    {caps.supportsTemplates
                      ? 'Só template aprovado reabre a conversa.'
                      : 'Este canal não tem template para reabrir.'}
                  </p>
                </div>
              )}

              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-700 max-h-72 overflow-y-auto">
                {mensagens.length === 0 ? (
                  <p className="text-xs text-slate-400 p-4 text-center">Sem mensagens.</p>
                ) : (
                  mensagens.map((m) => (
                    <div key={m.id} className="p-3 flex items-start gap-3">
                      <span
                        className={`shrink-0 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                          m.direction === 'outbound'
                            ? 'bg-primary/10 text-primary'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300'
                        }`}
                      >
                        {m.direction === 'outbound' ? 'saída' : 'entrada'}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-slate-700 dark:text-slate-200 break-words">
                          {m.body || <em className="text-slate-400">({m.type} sem corpo)</em>}
                        </p>
                        {m.failure_reason && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                            {m.failure_reason}
                          </p>
                        )}
                      </div>
                      {m.status && (
                        <span
                          className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                            CORES_STATUS[m.status] ?? 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {m.status}
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Campos dirigidos por capabilities. Hoje o WhatsApp não liga
                  nenhum dos dois; quando o e-mail entrar na Fase 7, aparecem
                  sozinhos. */}
              {caps.supportsSubject && (
                <input
                  value={assunto}
                  onChange={(e) => setAssunto(e.target.value)}
                  placeholder="Assunto"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-100"
                />
              )}
              {caps.supportsCc && (
                <input
                  value={copia}
                  onChange={(e) => setCopia(e.target.value)}
                  placeholder="Cópia (e-mails separados por vírgula)"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-100"
                />
              )}

              <div className="flex items-end gap-2">
                <textarea
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      enviar();
                    }
                  }}
                  rows={2}
                  placeholder="Mensagem... (Enter envia, Shift+Enter quebra linha)"
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-100 resize-none"
                />
                <button
                  onClick={enviar}
                  disabled={enviando || !texto.trim()}
                  className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors"
                >
                  {enviando ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Enviar
                </button>
              </div>

              {/* O 422 do assertCanSend chega aqui com a frase literal do
                  adapter. É o ponto que esta tela existe para provar. */}
              {erro && (
                <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg p-2.5">
                  {erro}
                </p>
              )}
              {aviso && (
                <p className="text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-2.5">
                  {aviso}
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ComposerTesteView;
